import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  LinearProgress,
  TextField,
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import {
  Bedtime as SleepIcon,
  Water as HydrationIcon,
  Restaurant as NutritionIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { ActivityFormData } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { activityService } from '../../services/activityService';
import HealthMetricsGuideModal from './HealthMetricsGuideModal';

// Extended type for health metrics callback - includes date for Activity modal
interface HealthMetricsSubmission {
  sleepScore: number;
  hydrationLevel: number;
  nutritionLevel: number;
  _submissionDate: string; // Date used (YYYY-MM-DD) - passed to Activity modal
}

interface HealthMetricsModalProps {
  open: boolean;
  onClose: () => void;
  onHealthMetricsSubmitted: (data: HealthMetricsSubmission) => void;
  teamId: string;
}

const HealthMetricsModal: React.FC<HealthMetricsModalProps> = ({
  open,
  onClose,
  onHealthMetricsSubmitted,
  teamId
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [guideModalType, setGuideModalType] = useState<'sleep' | 'hydration' | 'nutrition'>('sleep');
  const [selectedDate, setSelectedDate] = useState<string>('');

  // Form data state - only health metrics
  const [formData, setFormData] = useState({
    sleepScore: 0,
    hydrationLevel: 0,
    nutritionLevel: 0,
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        sleepScore: 0,
        hydrationLevel: 0,
        nutritionLevel: 0,
      });
      // Default to today's date for new health metrics entry
      setSelectedDate(activityService.getTodayDateString());
      setError('');
    }
  }, [open]);

  const handleStepperChange = (field: keyof typeof formData, delta: number) => {
    const max = 10;
    const min = 0;
    setFormData(prev => ({
      ...prev,
      [field]: Math.min(max, Math.max(min, prev[field] + delta)),
    }));
  };

  const handleSubmit = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      // Same 0–10 scale as DailyActivity / RunningActivityModal (not hours).
      const healthMetricsData = {
        sleepScore: formData.sleepScore,
        hydrationLevel: formData.hydrationLevel,
        nutritionLevel: formData.nutritionLevel,
      };

      // Check platform for iOS timeout wrapper
      const { Capacitor } = await import('@capacitor/core');
      const platform = Capacitor.getPlatform();

      let result;
      if (platform === 'ios') {
        console.log('[HealthMetricsModal] iOS detected - using timeout wrapper');
        // Add timeout wrapper for iOS
        const submitPromise = activityService.submitHealthMetrics(user.id, teamId, healthMetricsData, selectedDate || undefined);
        const timeoutPromise = new Promise<any>((_, reject) => 
          setTimeout(() => reject(new Error('Health metrics submission timed out')), 10000) // 10 second timeout
        );
        
        try {
          result = await Promise.race([submitPromise, timeoutPromise]);
          console.log('[HealthMetricsModal] Submission successful');
        } catch (timeoutError) {
          console.warn('[HealthMetricsModal] Submission timed out, but metrics may have been saved');
          // Assume success if timeout
          result = {
            success: true,
            message: 'Health metrics submitted successfully'
          };
        }
      } else {
        // Web/Android - use normal promise
        result = await activityService.submitHealthMetrics(user.id, teamId, healthMetricsData, selectedDate || undefined);
      }
      
      if (result.success) {
        // Call parent callback with health metrics data - date is embedded for Activity modal
        onHealthMetricsSubmitted({
          sleepScore: formData.sleepScore,
          hydrationLevel: formData.hydrationLevel,
          nutritionLevel: formData.nutritionLevel,
          _submissionDate: selectedDate || activityService.getTodayDateString(),
        });
        
        // Close modal
        onClose();
      } else {
        setError(result.message || 'Failed to submit health metrics');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit health metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenGuide = (type: 'sleep' | 'hydration' | 'nutrition') => {
    setGuideModalType(type);
    setShowGuideModal(true);
  };

  const handleCloseGuide = () => {
    setShowGuideModal(false);
  };

  // Quick selection button presets - optimized for one-tap selection (for score-based metrics)
  const quickSelectPresets = [
    { label: 'Poor', value: 2, color: '#d32f2f', range: '0-3' },
    { label: 'Fair', value: 5, color: '#f57c00', range: '4-6' },
    { label: 'Good', value: 7, color: '#388e3c', range: '7-8' },
    { label: 'Excellent', value: 10, color: '#2e7d32', range: '9-10' },
  ];
  // Hydration: brown → orange → yellow → light yellow (10 = light yellow)
  const HYDRATION_GRADIENT = 'linear-gradient(to right, #5D4037 0%, #8D6E63 20%, #E65100 40%, #FF9800 60%, #FFEB3B 80%, #FFF9C4 100%)';
  const hydrationPresets = [
    { label: 'Poor', value: 2, color: '#5D4037', range: '0-3' },
    { label: 'Fair', value: 5, color: '#E65100', range: '4-6' },
    { label: 'Good', value: 7, color: '#FFB300', range: '7-8' },
    { label: 'Excellent', value: 10, color: '#FFF9C4', range: '9-10' },
  ];

  /** Hour-style labels for quick pick; `value` is the 0–10 score stored (same as stepper / API). */
  const sleepQualityPresets = [
    { label: '4 hrs', value: 4, color: '#d32f2f' },
    { label: '6 hrs', value: 6, color: '#f57c00' },
    { label: '7 hrs', value: 7, color: '#388e3c' },
    { label: '8 hrs', value: 8, color: '#2e7d32' },
    { label: '9+ hrs', value: 10, color: '#2e7d32' },
  ];

  const handleQuickSelect = (field: keyof typeof formData, value: number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Optional: Skip a metric
  const handleSkip = (field: keyof typeof formData) => {
    setFormData(prev => ({ ...prev, [field]: 0 }));
  };

  // Get color for value (score-based metrics: nutrition)
  const getValueColor = (value: number): string => {
    if (value === 0) return '#9e9e9e';
    if (value < 4) return '#d32f2f'; // Red
    if (value < 7) return '#f57c00'; // Orange
    return '#2e7d32'; // Green
  };

  // Get color for hydration (matches gradient: brown → orange → yellow → light yellow)
  const getHydrationValueColor = (value: number): string => {
    if (value === 0) return '#9e9e9e';
    if (value <= 2) return '#5D4037';   // Brown
    if (value <= 4) return '#8D6E63';   // Brown-orange
    if (value <= 6) return '#E65100';   // Orange
    if (value <= 8) return '#FFB300';   // Yellow
    return '#FFF9C4';                    // Light yellow (9-10)
  };

  // Get label for value
  const getValueLabel = (value: number): string => {
    if (value === 0) return 'Not rated';
    if (value < 3) return 'Poor';
    if (value < 5) return 'Fair';
    if (value < 8) return 'Good';
    return 'Excellent';
  };

  // Calculate progress
  const progress = useMemo(() => {
    const completed = [formData.sleepScore, formData.hydrationLevel, formData.nutritionLevel]
      .filter(val => val > 0).length;
    return { completed, total: 3 };
  }, [formData]);

  // Render a single metric card
  const renderMetricCard = (
    field: 'sleepScore' | 'hydrationLevel' | 'nutritionLevel',
    title: string,
    description: string,
    icon: React.ReactNode,
    color: string,
    guideType: 'sleep' | 'hydration' | 'nutrition'
  ) => {
    const value = formData[field];
    const isHydration = field === 'hydrationLevel';
    const isSleep = field === 'sleepScore';
    const stepperMax = 10;
    const stepperMin = 0;
    const valueColor = isHydration
      ? getHydrationValueColor(value)
      : getValueColor(value);
    const valueLabel = getValueLabel(value);
    const presets = isHydration ? hydrationPresets : isSleep ? sleepQualityPresets : quickSelectPresets;
    const chipLabel = `${valueLabel} • ${value}/10`;

    const isPresetSelected = (preset: { label: string; value: number; color: string; range?: string }) => {
      if (isSleep) {
        if (preset.label === '9+ hrs') return value >= 9;
        return value === preset.value;
      }
      return value === preset.value ||
        (preset.value === 2 && value > 0 && value <= 3) ||
        (preset.value === 5 && value >= 4 && value <= 6) ||
        (preset.value === 7 && value >= 7 && value <= 8) ||
        (preset.value === 10 && value >= 9);
    };

    const needsStepperOutline = isHydration && value >= 9;

    return (
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: 3,
          }
        }}
      >
        <CardContent sx={{ py: 2, px: { xs: 2, sm: 2.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {icon}
              <Typography variant="h6" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, fontWeight: 600 }}>
                {title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <IconButton 
                onClick={() => handleOpenGuide(guideType)}
                size="small"
                sx={{ color, p: 0.5 }}
                title={`View ${title} Guide`}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
              {value > 0 && (
                <Button
                  onClick={() => handleSkip(field)}
                  size="small"
                  variant="text"
                  sx={{ 
                    color: 'text.secondary', 
                    p: 0.5,
                    minWidth: 'auto',
                    fontSize: '0.7rem',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                  title="Clear this metric"
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>

          {/* Description - Shorter and more concise */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.75rem', lineHeight: 1.3 }}>
            {description}
          </Typography>

          {/* Quick Selection Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 0.5, sm: 0.75 }, 
            mb: 1.5, 
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {presets.map((preset) => {
              const isSelected = isPresetSelected(preset);
              const isLightHydration = isHydration && preset.value === 10;
              const useDarkText = isLightHydration; // Always use black text for Excellent (readable when selected or not)
              return (
                <Button
                  key={preset.label}
                  variant={isSelected ? 'contained' : 'outlined'}
                  size={isMobile ? 'medium' : 'small'}
                  onClick={() => handleQuickSelect(field, preset.value)}
                  sx={{
                    minWidth: { xs: '60px', sm: 'auto' },
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.75, sm: 0.5 },
                    fontSize: { xs: '0.8rem', sm: '0.75rem' },
                    fontWeight: isSelected ? 600 : 500,
                    backgroundColor: isSelected ? preset.color : 'transparent',
                    borderColor: isLightHydration ? '#BDBDBD' : preset.color,
                    borderWidth: isSelected ? 0 : 1.5,
                    color: useDarkText ? 'rgba(0,0,0,0.87)' : isSelected ? 'white' : preset.color,
                    '&:hover': {
                      backgroundColor: isSelected ? preset.color : `${preset.color}15`,
                      borderColor: isLightHydration ? '#9e9e9e' : preset.color,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s',
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    maxWidth: { xs: 'none', sm: '120px' },
                  }}
                >
                  {preset.label}
                </Button>
              );
            })}
          </Box>

          {/* Stepper: tap − / + to change value (no slider, avoids scroll/drag issues) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 1.5,
              flex: 1,
              minHeight: { xs: '56px', sm: '48px' },
            }}
          >
            <IconButton
              onClick={() => handleStepperChange(field, -1)}
              disabled={value <= stepperMin}
              size="large"
              sx={{
                bgcolor: value <= stepperMin ? 'action.disabledBackground' : `${valueColor}20`,
                color: value <= stepperMin ? 'action.disabled' : valueColor,
                '&:hover': value > stepperMin ? { bgcolor: `${valueColor}30` } : undefined,
                ...(needsStepperOutline && {
                  '& .MuiSvgIcon-root': { filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))' },
                }),
              }}
              aria-label="Decrease"
            >
              <RemoveIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                minWidth: 48,
                textAlign: 'center',
                fontWeight: 700,
                color: valueColor,
                opacity: value === 0 ? 0.6 : 1,
                ...(needsStepperOutline && {
                  WebkitTextStroke: '1px rgba(0,0,0,0.35)',
                  textShadow: '0 0 1px rgba(0,0,0,0.3)',
                }),
              }}
            >
              {value > 0 ? `${value}/10` : '0'}
            </Typography>
            <IconButton
              onClick={() => handleStepperChange(field, 1)}
              disabled={value >= stepperMax}
              size="large"
              sx={{
                bgcolor: value >= stepperMax ? 'action.disabledBackground' : `${valueColor}20`,
                color: value >= stepperMax ? 'action.disabled' : valueColor,
                '&:hover': value < stepperMax ? { bgcolor: `${valueColor}30` } : undefined,
                ...(needsStepperOutline && {
                  '& .MuiSvgIcon-root': { filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))' },
                }),
              }}
              aria-label="Increase"
            >
              <AddIcon />
            </IconButton>
          </Box>

          {/* Current Value Display */}
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            {value > 0 ? (
              <Chip
                label={chipLabel}
                size="small"
                sx={{
                  backgroundColor: isHydration && value >= 9 ? `${valueColor}` : `${valueColor}15`,
                  color: isHydration && value >= 9 ? 'rgba(0,0,0,0.87)' : valueColor,
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  height: { xs: 32, sm: 28 },
                  border: `1.5px solid ${isHydration && value >= 9 ? '#BDBDBD' : `${valueColor}40`}`,
                  '& .MuiChip-label': {
                    px: 1.5,
                  },
                }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Not rated yet
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderHealthMetrics = () => (
    <Box>
      {/* Date Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Date</Typography>
          </Box>
          <TextField
            fullWidth
            type="date"
            label="Select the date for these health metrics"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: activityService.getTodayDateString(), // Prevent future dates
            }}
            helperText="Choose the date when these health metrics occurred (defaults to today)"
          />
        </CardContent>
      </Card>

      {/* Progress Indicator - Enhanced with progress bar */}
      <Box sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
          How was your day? Rate these health metrics:
        </Typography>
        
        {/* Progress Bar */}
        <Box sx={{ mb: 1.5, px: { xs: 1, sm: 0 } }}>
          <LinearProgress 
            variant="determinate" 
            value={(progress.completed / progress.total) * 100} 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: progress.completed === progress.total ? '#2e7d32' : '#1976d2',
                borderRadius: 3,
              }
            }} 
          />
        </Box>
        
        {/* Status Chips - More compact */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: { xs: 0.5, sm: 1 }, 
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 1
        }}>
          <Chip
            icon={formData.sleepScore > 0 ? <SleepIcon sx={{ fontSize: '1rem' }} /> : undefined}
            label={formData.sleepScore > 0 ? `Sleep ${formData.sleepScore}/10` : 'Sleep'}
            size="small"
            color={formData.sleepScore > 0 ? 'success' : 'default'}
            variant={formData.sleepScore > 0 ? 'filled' : 'outlined'}
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: 28, sm: 24 }
            }}
          />
          <Chip
            icon={formData.hydrationLevel > 0 ? <HydrationIcon sx={{ fontSize: '1rem' }} /> : undefined}
            label={formData.hydrationLevel > 0 ? `Hydration ${formData.hydrationLevel}/10` : 'Hydration'}
            size="small"
            color={formData.hydrationLevel > 0 ? 'success' : 'default'}
            variant={formData.hydrationLevel > 0 ? 'filled' : 'outlined'}
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: 28, sm: 24 }
            }}
          />
          <Chip
            icon={formData.nutritionLevel > 0 ? <NutritionIcon sx={{ fontSize: '1rem' }} /> : undefined}
            label={formData.nutritionLevel > 0 ? `Nutrition ${formData.nutritionLevel}/10` : 'Nutrition'}
            size="small"
            color={formData.nutritionLevel > 0 ? 'success' : 'default'}
            variant={formData.nutritionLevel > 0 ? 'filled' : 'outlined'}
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: 28, sm: 24 }
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
          {progress.completed} of {progress.total} metrics rated {progress.completed < progress.total && '• All three metrics required'}
        </Typography>
      </Box>

      {/* Metrics Grid - Responsive layout with better spacing */}
      <Grid
        container
        spacing={{ xs: 2, sm: 2, md: 2 }}
        sx={{
          '& .MuiGrid-item': {
            minHeight: { xs: '240px', sm: '220px', md: '200px' }
          }
        }}
      >
        <Grid item xs={12} md={4}>
          {renderMetricCard(
            'sleepScore',
            'Sleep quality',
            'Rate rest from 1–10 (stepper), or tap a typical night (hours) as a quick starting point.',
            <SleepIcon sx={{ color: 'primary.main', fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />,
            theme.palette.primary.main,
            'sleep'
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderMetricCard(
            'hydrationLevel',
            'Hydration Level',
            'How well did you stay hydrated today?',
            <HydrationIcon sx={{ color: 'info.main', fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />,
            theme.palette.info.main,
            'hydration'
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderMetricCard(
            'nutritionLevel',
            'Nutrition Level',
            'How well did you eat today?',
            <NutritionIcon sx={{ color: 'success.main', fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />,
            theme.palette.success.main,
            'nutrition'
          )}
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ textAlign: 'center', flex: 2 }}>
              <Typography variant="h5" gutterBottom>
                Daily Health Metrics
              </Typography>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={onClose}
                disabled={loading}
                sx={{ minWidth: 'auto', p: 1 }}
                color="inherit"
              >
                ✕
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ 
          minHeight: { xs: 450, sm: 400 }, 
          maxHeight: '75vh', 
          overflow: 'auto',
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 }
        }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {renderHealthMetrics()}
        </DialogContent>

        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          justifyContent: 'space-between',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button
            onClick={onClose}
            disabled={loading}
            color="inherit"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Cancel' : 'Close'}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || progress.completed !== progress.total}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
            sx={{
              fontWeight: 600,
              minHeight: { xs: 44, sm: 36 }
            }}
          >
            {loading 
              ? 'Submitting...' 
              : progress.completed === 0 
                ? 'Rate all three metrics' 
                : progress.completed < progress.total
                  ? `Rate all three metrics (${progress.completed}/${progress.total} completed)`
                  : 'Submit Health Metrics'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Health Metrics Guide Modal */}
      <HealthMetricsGuideModal
        open={showGuideModal}
        onClose={handleCloseGuide}
        metricType={guideModalType}
      />
    </>
  );
};

export default HealthMetricsModal;
